import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { LoggingService } from './logging/logging.service';

/**
 * HttpInterceptor that adds CSRF token from cookie to headers for non-GET requests
 * Works with Spring Security's CookieCsrfTokenRepository
 */
@Injectable()
export class CsrfInterceptor implements HttpInterceptor {
  // The default name Spring Security uses for CSRF tokens
  private readonly CSRF_COOKIE_NAME = 'XSRF-TOKEN';
  private readonly CSRF_HEADER_NAME = 'X-XSRF-TOKEN';

  constructor(
    private cookieService: CookieService,
    private logger: LoggingService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only add CSRF protection for non-GET requests that target our API
    if (req.method !== 'GET' && this.isApiRequest(req.url)) {
      const csrfToken = this.getCsrfToken();
      
      if (csrfToken) {
        this.logger.debug('CsrfInterceptor', `Adding CSRF token for ${req.method} ${req.url}`);
        req = req.clone({
          headers: req.headers.set(this.CSRF_HEADER_NAME, csrfToken)
        });
      } else {
        this.logger.debug('CsrfInterceptor', `No CSRF token available for ${req.method} ${req.url}`);
      }
    }
    
    return next.handle(req);
  }

  /**
   * Get the CSRF token from the cookie
   */
  private getCsrfToken(): string | null {
    try {
      const token = this.cookieService.get(this.CSRF_COOKIE_NAME);
      return token || null;
    } catch (error) {
      this.logger.warn('CsrfInterceptor', 'Error accessing CSRF cookie', error);
      return null;
    }
  }

  /**
   * Determine if the request is to our API
   */
  private isApiRequest(url: string): boolean {
    // Check if it's a relative URL or matches our API domain
    return url.startsWith('/') || 
           url.includes('/api/') || 
           url.includes('/auth/');
  }
}
